/* ===================================================================
   useWidget() + useUpdateWidget() -- review-showcase widget config
   (Brad, 2026-06-17).

   Read:  GET /api/v1/widget  (lazily creates the default row + embed_key)
   Write: PUT /api/v1/widget

   The Widget screen binds to these. The embed snippet uses the real
   per-business embed_key. Read hook disabled until a Bearer rr_ token
   exists. After a save we invalidate so the config reflects the save.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type SendInstallInput,
  type SendInstallResponse,
  type WidgetResponse,
  type WidgetUpdate,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useWidget(): UseQueryResult<WidgetResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.widget(),
    queryFn: ({ signal }) => api.getWidget(signal),
    enabled: hasToken,
    staleTime: 60_000,
  });
}

export interface UseUpdateWidgetResult {
  save: (patch: WidgetUpdate) => Promise<WidgetResponse>;
  isPending: boolean;
  error: Error | null;
}

export function useUpdateWidget(): UseUpdateWidgetResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (patch: WidgetUpdate) => api.updateWidget(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.widget() });
    },
  });
  return {
    save: (patch) => m.mutateAsync(patch),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

export interface UseSendInstallResult {
  send: (input: SendInstallInput) => Promise<SendInstallResponse>;
  isPending: boolean;
  error: Error | null;
}

export function useSendInstall(): UseSendInstallResult {
  const m = useMutation({
    mutationFn: (input: SendInstallInput) => api.sendWidgetInstall(input),
  });
  return {
    send: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
